/**
 * Outbox repository: the queue of local mutations waiting for the server.
 *
 * Every local write that must reach the server goes through `recordChange`,
 * which writes the entity and its outbox row in ONE Dexie transaction — so
 * the UI can never show a change that is not queued, and a queued change can
 * never refer to a write that did not happen.
 */
import Dexie, { type Table } from 'dexie';

import type { ConflictInfo, MutationOperation } from '@/lib/api/types';
import { assertWritable } from '@/lib/auth/view-as';
import { uuidv7 } from '@/lib/id';

import { db, type OutboxRecord, type OutboxState } from './db';
import { guardStorage } from './quota';

/**
 * `work` means there is newly sendable work (worth an automatic sync);
 * `state` means only counts changed (worth a status refresh, not a sync).
 */
export type OutboxChange = 'work' | 'state';

const listeners = new Set<(change: OutboxChange) => void>();

/** Notified after outbox changes (sync status counts, debounced auto-sync). */
export function subscribeToOutbox(listener: (change: OutboxChange) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(change: OutboxChange = 'state'): void {
  listeners.forEach((listener) => listener(change));
}

export type MutationInput = {
  entity_type: string;
  entity_id: string;
  operation: MutationOperation;
  base_version: number | null;
  payload: Record<string, unknown>;
  project_id: string | null;
  summary: string;
};

export function buildRecord(input: MutationInput, now: Date): OutboxRecord {
  return {
    ...input,
    mutation_id: uuidv7(now.getTime()),
    created_at_device: now.toISOString(),
    attempts: 0,
    state: 'pending',
    last_error: null,
    next_attempt_at: null,
    conflict: null,
  };
}

/**
 * Apply a local write and queue its mutation atomically.
 *
 * @param tables  the entity tables `apply` touches (outbox is added automatically)
 * @param apply   the local write; runs inside the transaction
 */
export async function recordChange(
  tables: Table[],
  apply: () => Promise<unknown>,
  mutation: MutationInput,
  options: { mutationId?: string; now?: Date } = {},
): Promise<OutboxRecord> {
  // Viewing as someone (staff, read-only): nothing is written or queued on their behalf.
  assertWritable();
  const record = buildRecord(mutation, options.now ?? new Date());
  if (options.mutationId) record.mutation_id = options.mutationId;
  // Out of space → nothing of this change is written; earlier work is intact.
  await guardStorage(() =>
    db.transaction('rw', [...tables, db.outbox], async () => {
      await apply();
      await db.outbox.add(record);
    }),
  );
  // Inside a caller's transaction (a bulk check-in queues one mutation per
  // worker in one transaction), listeners must not run in its zone: their
  // reads (sync counts, the media queue) would join it — touching tables
  // outside its scope fails and loses the rest of the batch. Tell them once
  // we are outside.
  if (Dexie.currentTransaction) setTimeout(() => notify('work'), 0);
  else notify('work');
  return record;
}

export type OutboxCounts = Record<OutboxState, number>;

export async function countByState(): Promise<OutboxCounts> {
  const counts: OutboxCounts = {
    pending: 0,
    in_flight: 0,
    applied: 0,
    conflict: 0,
    rejected: 0,
    failed: 0,
  };
  await db.outbox.each((record) => {
    counts[record.state] += 1;
  });
  return counts;
}

/** Mutations not yet confirmed by the server (anything a logout would lose). */
export async function unsyncedCount(): Promise<number> {
  return db.outbox.where('state').noneOf(['applied']).count();
}

/** Oldest first. Ordered by mutation_id (UUIDv7, monotonic) — not by the
 * device clock, which ties within a millisecond and can be changed by the user. */
export function listAll(): Promise<OutboxRecord[]> {
  return db.outbox.toArray();
}

export function listForEntity(entityType: string, entityId: string): Promise<OutboxRecord[]> {
  return db.outbox.where('[entity_type+entity_id]').equals([entityType, entityId]).toArray();
}

/**
 * The next batch to push, oldest first.
 *
 * A mutation is held back while an earlier mutation for the same entity is
 * unresolved (conflict/rejected) or not yet eligible — replaying a later edit
 * on top of a refused earlier one would apply changes out of order.
 */
export async function takeBatch(limit: number, now: number): Promise<OutboxRecord[]> {
  const ordered = await listAll();
  const blocked = new Set<string>();
  const batch: OutboxRecord[] = [];
  for (const record of ordered) {
    const key = `${record.entity_type}:${record.entity_id}`;
    const eligible =
      record.state === 'pending' ||
      (record.state === 'failed' && (record.next_attempt_at ?? 0) <= now);
    if (blocked.has(key) || !eligible) {
      if (record.state !== 'applied') blocked.add(key);
      continue;
    }
    batch.push(record);
    if (batch.length >= limit) break;
  }
  return batch;
}

export async function markInFlight(ids: string[]): Promise<void> {
  await db.outbox.where('mutation_id').anyOf(ids).modify({ state: 'in_flight' });
  notify();
}

/** Crash recovery: anything left in flight by a closed tab goes back to pending. */
export async function recoverInFlight(): Promise<void> {
  await db.outbox.where('state').equals('in_flight').modify({ state: 'pending' });
}

export async function returnToPending(ids: string[]): Promise<void> {
  await db.outbox.where('mutation_id').anyOf(ids).modify({ state: 'pending' });
  notify();
}

const BACKOFF_STEPS_MS = [15_000, 60_000, 5 * 60_000, 15 * 60_000, 60 * 60_000];

export async function markFailed(
  ids: string[],
  error: { code: string; message: string },
  now: number,
): Promise<void> {
  await db.outbox
    .where('mutation_id')
    .anyOf(ids)
    .modify((record) => {
      record.attempts += 1;
      record.state = 'failed';
      record.last_error = error;
      const step = BACKOFF_STEPS_MS[Math.min(record.attempts - 1, BACKOFF_STEPS_MS.length - 1)];
      record.next_attempt_at = now + (step ?? 0);
    });
  notify();
}

/** When the soonest failed mutation may be retried (`null`: none is waiting on a backoff). */
export async function nextAttemptAt(): Promise<number | null> {
  const failed = await db.outbox.where('state').equals('failed').toArray();
  const times = failed.flatMap((record) =>
    record.next_attempt_at === null ? [] : [record.next_attempt_at],
  );
  return times.length ? Math.min(...times) : null;
}

/** Server confirmed (applied or duplicate): the mutation leaves the queue. */
export async function complete(id: string): Promise<void> {
  await db.outbox.delete(id);
}

export async function markConflict(id: string, conflict: ConflictInfo | null): Promise<void> {
  await db.outbox.update(id, {
    state: 'conflict',
    conflict,
    last_error: { code: 'CONFLICT', message: 'Changed on the server since you edited it.' },
  });
}

export async function markRejected(
  id: string,
  error: { code: string; message: string },
): Promise<void> {
  await db.outbox.update(id, { state: 'rejected', last_error: error });
}

/** User asked to retry from the Sync Center. */
export async function retry(id: string): Promise<void> {
  await db.outbox.update(id, { state: 'pending', next_attempt_at: null });
  notify('work');
}

export async function retryAllFailed(): Promise<void> {
  await db.outbox
    .where('state')
    .equals('failed')
    .modify({ state: 'pending', next_attempt_at: null });
  notify('work');
}

/**
 * Replace `record` (and every later unconfirmed mutation for the same entity)
 * with fresh mutation ids, in order. Re-sending an id the server has already
 * answered would only get the old answer back (`duplicate`), so a resolved
 * conflict is a NEW mutation. Call inside a transaction that includes the outbox.
 *
 * @param replacement  what to send instead of `record` (omit to drop it)
 */
export async function requeueAfterResolution(
  record: OutboxRecord,
  replacement: Partial<MutationInput> | null,
  extra: MutationInput[] = [],
): Promise<void> {
  const later = (
    await db.outbox
      .where('[entity_type+entity_id]')
      .equals([record.entity_type, record.entity_id])
      .toArray()
  )
    .filter((row) => row.mutation_id > record.mutation_id && row.state !== 'applied')
    .sort((a, b) => (a.mutation_id < b.mutation_id ? -1 : 1));
  await db.outbox.bulkDelete([record.mutation_id, ...later.map((row) => row.mutation_id)]);
  const now = new Date();
  const inputs: MutationInput[] = [];
  if (replacement) {
    inputs.push({
      entity_type: record.entity_type,
      entity_id: record.entity_id,
      operation: record.operation,
      base_version: record.base_version,
      payload: record.payload,
      project_id: record.project_id,
      summary: record.summary,
      ...replacement,
    });
  }
  inputs.push(...extra);
  for (const input of inputs) await db.outbox.add(buildRecord(input, now));
  for (const row of later) {
    await db.outbox.add({ ...row, ...buildRecord(row, now), attempts: 0 });
  }
}

/** Drop a mutation the server refused (or that the user discards). */
export async function discard(id: string): Promise<OutboxRecord | undefined> {
  const record = await db.outbox.get(id);
  await db.outbox.delete(id);
  notify();
  return record;
}

export function notifyOutboxChanged(change: OutboxChange = 'state'): void {
  notify(change);
}
